import { GRADES, gradeLabel, isGrade, type SchoolRules } from "@vavr/core";
// Site settings: attestation texts, drop-off signature, adult PINs, closing time; retention and
// legal hold; and the signed-in staff member's own kiosk PIN.
import { useState } from "react";
import { STATE_RETENTION, DEFAULT_RETENTION_DAYS, CACFP_DAYS } from "@vavr/core";
import { ThemeChoice, Banner, Button, Card, Select, Switch, TextField } from "@vavr/ui";
import { api, navigate } from "../shared/index.ts";
import { useConsole, useGet, useAction } from "./data.tsx";
import { LoadError, Loading } from "./common.tsx";

interface SiteSettings extends SchoolRules { dropoff_signature: "required" | "optional" | "off"; adult_pins: boolean; attestation_pickup: string; attestation_dropoff: string; closing_time: string | null; app_pickup_request?: boolean; app_dropoff_request?: boolean }
interface SiteInfo { site: { id: string; name: string; state: string; tz: string; retention_days: number; legal_hold: boolean; settings: SiteSettings } }

export function Settings() {
  const { site } = useConsole();
  return site.org_kind === "operator" ? <DepotSettings /> : <SchoolSettings />;
}

/**
 * A bus company's depot: no school rules, door texts or pickup records here (they belong to the
 * schools it serves). Its own operation is set on the Transport page.
 */
function DepotSettings() {
  const { can } = useConsole();
  return (
    <div className="pmu-stack pmu-c-narrow">
      <Card title="Appearance" description="Light, dark, or follow this computer's setting. Saved on this browser."><ThemeChoice /></Card>
      {can("transport:manage") && (
        <Card title="Your bus company" description="Everything about running your buses is on the Transport page.">
          <div className="pmu-stack"><dl className="pmu-kv">
            <dt>Days off and changes</dt><dd>Transport, Calendar</dd>
            <dt>Drivers and time off</dt><dd>Transport, Drivers</dd>
            <dt>Inspections and insurance</dt><dd>Transport, Compliance</dd>
            <dt>Schools you serve and your code</dt><dd>Transport, Schools</dd>
          </dl>
          <div><Button variant="secondary" icon="bus" onClick={() => navigate("/console/transport")}>Open Transport</Button></div></div>
        </Card>
      )}
      <Card title="School rules and records" description="Pickup rules, door texts and how long pickup records are kept are each school's own settings. A school sets them in its console; your drivers follow them on its routes." />
    </div>
  );
}

function SchoolSettings() {
  const { can } = useConsole();
  // A school's own bus crew (dispatcher, driver) reads none of the school's records: appearance only.
  if (!can("roster.read")) return <div className="pmu-stack pmu-c-narrow"><Card title="Appearance" description="Light, dark, or follow this computer's setting. Saved on this browser."><ThemeChoice /></Card></div>;
  return <SchoolSiteSettings />;
}

function SchoolSiteSettings() {
  const { base, can, me, site } = useConsole();
  const info = useGet<SiteInfo>(base);
  const staffHere = me.memberships.some((m) => ["org_admin", "site_admin", "door_staff", "teacher"].includes(m.role) && m.org_id === site.org_id && (m.site_id === null || m.site_id === site.id));
  if (info.loading) return <Loading />;
  if (info.error || !info.data) return <LoadError error={info.error ?? new Error("Not found")} retry={info.reload} />;
  return (
    <div className="pmu-stack pmu-c-narrow">
      <Card title="Appearance" description="Light, dark, or follow this computer's setting. Saved on this browser."><ThemeChoice /></Card>
      {can("site.configure") && <RulesForm key={`r${JSON.stringify(info.data.site.settings)}`} info={info.data} />}
      {can("site.configure") && <SiteForm key={JSON.stringify(info.data.site.settings)} info={info.data} />}
      <Retention info={info.data} />
      {staffHere && <KioskPin />}
    </div>
  );
}

function SiteForm({ info }: { info: SiteInfo }) {
  const { base } = useConsole();
  const [f, setF] = useState<SiteSettings>(info.site.settings);
  const act = useAction();
  const ca = info.site.state === "CA";
  return (
    <Card title="At the door" description="What kiosks and phones ask for. Devices pick up changes on their next sync.">
      <form className="pmu-stack" onSubmit={async (e) => { e.preventDefault(); await act.run(() => api("PATCH", `${base}/settings`, { ...f, closing_time: f.closing_time || null }), { success: "Settings saved.", invalidate: [base] }); }}>
        {act.error && <Banner tone="danger" title={act.error} />}
        <TextField label="Pickup attestation" hint="Shown above the signature pad, and stored word for word with every pickup." value={f.attestation_pickup} maxLength={500} onChange={(e) => setF({ ...f, attestation_pickup: e.target.value })} />
        <TextField label="Drop-off attestation" value={f.attestation_dropoff} maxLength={500} onChange={(e) => setF({ ...f, attestation_dropoff: e.target.value })} />
        <Select label="Signature at drop-off" value={ca ? "required" : f.dropoff_signature} disabled={ca} onValueChange={(v) => setF({ ...f, dropoff_signature: (v ?? "optional") as SiteSettings["dropoff_signature"] })}
          hint={ca ? "California requires a full signature at drop-off and pickup (22 CCR §101229.1), so this stays on." : undefined}
          options={[{ value: "required", label: "Required" }, { value: "optional", label: "Optional" }, { value: "off", label: "Off" }]} />
        <Switch label="Adults enter a PIN at the door" description="Each authorized adult gets a 4-digit code. Texas expects a unique identifier code." checked={f.adult_pins} onCheckedChange={(c) => setF({ ...f, adult_pins: c })} />
        <Switch label="Parents can ask for a pickup without scanning" description="From the parent app, with their photo and signature. Whoever has the child brings them to the front or sends the parent to the room. Off: parents scan the door code." checked={f.app_pickup_request ?? false} onCheckedChange={(c) => setF({ ...f, app_pickup_request: c })} />
        <Switch label="Parents can drop off without scanning" description="From the parent app. Nothing is recorded until the class teacher confirms the child is with them. Off: parents scan the door code." checked={f.app_dropoff_request ?? true} onCheckedChange={(c) => setF({ ...f, app_dropoff_request: c })} />
        <TextField label="Closing time" type="time" hint="Pickups after this are listed as late. Leave blank if you do not track late pickups." value={f.closing_time ?? ""} onChange={(e) => setF({ ...f, closing_time: e.target.value || null })} />
        <div><Button type="submit" loading={act.busy}>Save</Button></div>
      </form>
    </Card>
  );
}

/** The school's rules (docs/spec/school-rules.md): what each step asks for, some from a grade up. */
function RulesForm({ info }: { info: SiteInfo }) {
  const { base } = useConsole();
  const st = info.site.settings;
  const [f, setF] = useState<SchoolRules>({
    walk_in_from: st.walk_in_from ?? null, bus_walk_from: st.bus_walk_from ?? null, pickup_signature: st.pickup_signature ?? "required", pickup_photo: st.pickup_photo ?? "required",
    home_alone_from: st.home_alone_from ?? null, sibling_from: st.sibling_from ?? null, bus_changes_close: st.bus_changes_close ?? null, late_after: st.late_after ?? null,
  });
  const act = useAction();
  const fromOpts = [{ value: "never", label: "Never" }, ...GRADES.map((g) => ({ value: g, label: `${gradeLabel(g)} and up` }))];
  const pick = (v: string | null) => (v && v !== "never" && isGrade(v) ? v : null);
  return (
    <Card title="School rules" description="How strict each step is. Grades come from each room (or a child's own). A restriction on file always wins.">
      <form className="pmu-stack" onSubmit={async (e) => { e.preventDefault(); await act.run(() => api("PATCH", `${base}/settings`, f), { success: "Rules saved.", invalidate: [base] }); }}>
        {act.error && <Banner tone="danger" title={act.error} />}
        <Select label="Children may come in on their own" value={f.walk_in_from ?? "never"} onValueChange={(v) => setF({ ...f, walk_in_from: pick(v) })} options={fromOpts}
          hint="Their teacher marks them here in the staff app (Arrived on their own). Younger children need an adult to sign them in." />
        <Select label="Children may leave the bus without an adult" value={f.bus_walk_from ?? "never"} onValueChange={(v) => setF({ ...f, bus_walk_from: pick(v) })} options={fromOpts}
          hint="The default for each rider. A parent can choose otherwise for their child, with a reason the office and the driver see." />
        <Select label="Children may go home on their own" value={f.home_alone_from ?? "never"} onValueChange={(v) => setF({ ...f, home_alone_from: pick(v) })} options={fromOpts}
          hint="Walking or biking home, with their parent's consent in the app (or a paper form the office enters). Staff mark them 'Went home on their own'." />
        <Select label="An older brother or sister may collect" value={f.sibling_from ?? "never"} onValueChange={(v) => setF({ ...f, sibling_from: pick(v) })} options={fromOpts}
          hint="The grade the sibling (a pupil here) must be in, with the parent's consent. Siblings who are not pupils here are added as approved adults instead." />
        <TextField label="Same-day bus changes close at" type="time" value={f.bus_changes_close ?? ""} onChange={(e) => setF({ ...f, bus_changes_close: e.target.value || null })}
          hint="Until then a parent's 'Not on the bus today' goes straight to the driver. Later ones go to the office too, to confirm. Leave blank for no cut-off." />
        <TextField label="Arrivals are late after" type="time" value={f.late_after ?? ""} onChange={(e) => setF({ ...f, late_after: e.target.value || null })}
          hint="A drop-off after this time is recorded as a late arrival (the board and the parent see it). Leave blank if you do not track it." />
        <Select label="Signature at pickup" value={f.pickup_signature} onValueChange={(v) => setF({ ...f, pickup_signature: v === "optional" ? "optional" : "required" })}
          options={[{ value: "required", label: "Required" }, { value: "optional", label: "Optional: the adult can skip it" }]} />
        <Select label="Photo at pickup" value={f.pickup_photo} onValueChange={(v) => setF({ ...f, pickup_photo: v === "optional" ? "optional" : "required" })}
          options={[{ value: "required", label: "Required: a face in the photo" }, { value: "optional", label: "Optional: go ahead without one (flagged)" }]} />
        <div><Button type="submit" loading={act.busy}>Save rules</Button></div>
      </form>
    </Card>
  );
}

function Retention({ info }: { info: SiteInfo }) {
  const { base, can } = useConsole();
  const act = useAction();
  const s = info.site;
  const rule = STATE_RETENTION[s.state];
  const years = (d: number) => { const n = Math.floor(d / 365); return n >= 1 ? `about ${n} year${n > 1 ? "s" : ""}` : `${d} days`; };
  return (
    <Card title="Record keeping" description="How long pickup records are kept before their personal details are removed. The chain of hashes is kept, so the record's existence stays provable.">
      <div className="pmu-stack">
        <dl className="pmu-kv">
          <dt>Kept for</dt><dd><strong>{s.retention_days} days</strong> ({years(s.retention_days)})</dd>
          <dt>Why</dt><dd>
            {rule ? `${s.state} minimum: ${rule.days} days (${rule.source}). ` : `${s.state} sets no shorter minimum for pickup records. `}
            Vavr keeps at least {DEFAULT_RETENTION_DAYS} days everywhere; School and District plans keep 5 years; food program (CACFP) sites keep 3 years plus the current fiscal year ({CACFP_DAYS} days). The longest rule that applies wins. To change it, contact support.
          </dd>
        </dl>
        {act.error && <Banner tone="danger" title={act.error} />}
        {can("retention.manage") ? (
          <Switch label="Legal hold" description="Nothing is removed while a hold is on, even past the retention period. Use it when records may be needed for a dispute or investigation."
            checked={s.legal_hold} disabled={act.busy}
            onCheckedChange={(on) => act.run(() => api("POST", `${base}/legal-hold`, { on }), { success: on ? "Legal hold is on." : "Legal hold is off.", invalidate: [base] })} />
        ) : <p>Legal hold: <strong>{s.legal_hold ? "on" : "off"}</strong></p>}
      </div>
    </Card>
  );
}

function KioskPin() {
  const { base } = useConsole();
  const [pin, setPin] = useState("");
  const act = useAction();
  const ok = /^\d{4}$/.test(pin);
  return (
    <Card title="My kiosk PIN" description="You enter this on a kiosk to start a shift or witness an override. Only you should know it.">
      <form className="pmu-row pmu-c-pin" onSubmit={async (e) => { e.preventDefault(); if (await act.run(() => api("POST", `${base}/me/pin`, { pin }), { success: "PIN saved. Kiosks get it on their next sync." })) setPin(""); }}>
        <TextField label="New 4-digit PIN" type="password" inputMode="numeric" autoComplete="off" maxLength={4} pattern="\d{4}" value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} error={act.error ?? undefined} hint="Not all the same digit, and not 1234." />
        <Button type="submit" disabled={!ok} loading={act.busy}>Set PIN</Button>
      </form>
    </Card>
  );
}
